import { useMemo, useState } from "react";
import { Card, Col, Form, Image, Row, Button, Pagination } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BackButton from "../Components/BackButton";
import Loader from "../Components/Loader";
import Message from "../Components/Message";
import PriceWithSale from "../Components/PriceWithSale";
import { useGetProductsQuery } from "../slices/productsApiSlice";
import { addToCart } from "../slices/cartSlice";
import { getEffectivePrice } from "../utils/salePricing";
import { getProductDisplayName } from "../utils/productTitle";

export default function BulkOrderScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth || {});
  const isTieredUser = Boolean(userInfo?.tier?.id);

  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [quantities, setQuantities] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const query = useMemo(
    () => ({
      keyword: appliedSearch || undefined,
      pageNumber,
    }),
    [appliedSearch, pageNumber]
  );

  const { data, isLoading, error } = useGetProductsQuery(query);
  const products = data?.products ?? [];
  const pages = data?.pages || 1;
  const currentPage = data?.page || pageNumber;

  const handleSubmit = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm.trim());
    setPageNumber(1);
  };

  const handleClear = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setPageNumber(1);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const fetchSuggestions = async (term) => {
    try {
      if (!term || term.trim().length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      const res = await fetch(
        `/api/products?keyword=${encodeURIComponent(term)}&pageNumber=1`
      );
      const data = await res.json();
      const list = (data.products || []).slice(0, 6);
      setSuggestions(list);
      setShowSuggestions(list.length > 0);
    } catch (err) {
      console.error("Suggestion fetch error", err);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleQtyChange = (productId, value) => {
    const numeric = Number(value);
    setQuantities((prev) => ({
      ...prev,
      [productId]: Number.isFinite(numeric) ? numeric : "",
    }));
  };

  const getQty = (productId) => {
    const qty = quantities[productId];
    return qty === "" ? "" : qty ?? 1;
  };

  const addItem = (product) => {
    if ((product.status || 'active') !== 'active') {
      toast.error('This product is not available for purchase yet.');
      return;
    }
    const displayName = product.displayName || getProductDisplayName(product);
    const basePrice = Number(product.price ?? 0);
    const tierPrice =
      isTieredUser && product?.tierPrice != null
        ? Number(product.tierPrice)
        : null;
    const effectivePrice = isTieredUser
      ? tierPrice ?? basePrice
      : getEffectivePrice({
          price: basePrice,
          salePrice: product.salePrice,
          saleStartAt: product.saleStartAt,
          saleEndAt: product.saleEndAt,
        });
    const tierPriceApplied = Boolean(isTieredUser && tierPrice != null);
    const rawQty = Number(quantities[product._id] ?? 1);
    const safeQty = Math.max(1, Number.isFinite(rawQty) ? rawQty : 1);

    dispatch(
      addToCart({
        ...product,
        qty: safeQty,
        price: effectivePrice,
        originalPrice: basePrice,
        salePrice: product.salePrice != null ? Number(product.salePrice) : 0,
        saleStartAt: product.saleStartAt,
        saleEndAt: product.saleEndAt,
        tierPrice: tierPriceApplied ? tierPrice : null,
        tierPriceApplied,
        ignoreSale: isTieredUser,
      })
    );

    toast.success(`${displayName} added (${safeQty})`);
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  if (!isTieredUser) {
    return (
      <div className="bulk-order-page">
        <BackButton className="btn btn-light btn-sm my-3" />
        <Card className="bulk-order-locked border-0 shadow-sm">
          <Card.Body>
            <h1 className="h4 mb-2">Bulk Ordering</h1>
            <p className="text-muted mb-3">
              Bulk ordering is reserved for partnered customers. Redeem your
              partner code to unlock wholesale pricing and bulk add tools.
            </p>
            <div className="d-flex flex-column flex-sm-row gap-2">
              <Button variant="primary" onClick={() => navigate("/profile")}>
                Go to profile
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate("/browse")}>
                Back to browse parts
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="bulk-order-page">
      <header className="bulk-order-hero mb-4">
        <div className="bulk-order-hero-content">
          <p className="text-uppercase small text-muted mb-2">Partner customers</p>
          <h1 className="mb-2">Bulk order in minutes</h1>
          <p className="text-muted mb-0">
            Search by name, SKU, or part number, enter quantities, and add
            items directly to your cart with wholesale pricing.
          </p>
        </div>
        <div className="bulk-order-hero-steps">
          <div className="bulk-order-step">
            <span>1</span>
            <div>
              <div className="fw-semibold">Find products</div>
              <small className="text-muted">Search by part number or SKU.</small>
            </div>
          </div>
          <div className="bulk-order-step">
            <span>2</span>
            <div>
              <div className="fw-semibold">Enter quantities</div>
              <small className="text-muted">Enter any quantity per item.</small>
            </div>
          </div>
          <div className="bulk-order-step">
            <span>3</span>
            <div>
              <div className="fw-semibold">Checkout faster</div>
              <small className="text-muted">Cart totals update instantly.</small>
            </div>
          </div>
        </div>
      </header>

      <Card className="border-0 shadow-sm mb-4 bulk-order-search">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="g-3 align-items-end">
              <Col xs={12} lg={6}>
                <Form.Label className="mb-1">Search products</Form.Label>
                <div className="header-search-wrapper bulk-order-search-wrapper">
                  <Form.Control
                    type="search"
                    placeholder="Brake pads, 208916, or SKU"
                    value={searchTerm}
                    onChange={(e) => {
                      const term = e.target.value;
                      setSearchTerm(term);
                      fetchSuggestions(term);
                    }}
                    onFocus={() => {
                      if (suggestions.length > 0) setShowSuggestions(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowSuggestions(false), 150);
                    }}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="search-suggestions">
                      {suggestions.map((product) => {
                        const displayName = product.displayName || getProductDisplayName(product);
                        return (
                          <button
                            type="button"
                            key={product._id}
                            className="search-suggestion-item"
                            onMouseDown={() => {
                              const nextTerm = displayName || "";
                              setSearchTerm(nextTerm);
                              setAppliedSearch(nextTerm);
                              setPageNumber(1);
                              setShowSuggestions(false);
                            }}
                          >
                            {displayName}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={12} lg={6} className="d-flex flex-column flex-sm-row gap-2">
                <Button type="submit" variant="primary">
                  Search
                </Button>
                <Button type="button" variant="outline-secondary" onClick={handleClear}>
                  Clear
                </Button>
                <Button type="button" variant="outline-primary" onClick={() => navigate("/cart")}>
                  Review cart
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {isLoading && <Loader />}
      {error && (
        <Message variant="danger">
          {error?.data?.message || error.error}
        </Message>
      )}

      {!isLoading && !error && (
        <div className="bulk-order-results">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
            <div>
              <h2 className="h5 mb-1">Results</h2>
              <p className="text-muted mb-0">
                Showing {products.length} items
                {appliedSearch ? ` for “${appliedSearch}”` : "."}
              </p>
            </div>
          </div>

          {products.length === 0 ? (
            <Message>
              No products found. Try a different keyword or browse all products.
            </Message>
          ) : (
            <>
              <div className="bulk-order-grid">
                {products.map((product) => {
                  const displayName = product.displayName || getProductDisplayName(product);
                  const tierPrice =
                    isTieredUser && product?.tierPrice != null
                      ? Number(product.tierPrice)
                      : null;
                  return (
                    <Card key={product._id} className="bulk-order-card border-0 shadow-sm">
                      <Card.Body>
                        <div className="bulk-order-card-header">
                          <div className="bulk-order-thumb">
                            <Image
                              src={product.image}
                              alt={displayName}
                              onError={(e) => (e.currentTarget.style.display = "none")}
                              fluid
                            />
                          </div>
                          <div className="bulk-order-meta">
                            <h3 className="h6 mb-1">{displayName}</h3>
                            <div className="text-muted small d-flex flex-wrap gap-3">
                              {product.partNumber && (
                                <span>Part #{product.partNumber}</span>
                              )}
                              {product.sku && <span>SKU {product.sku}</span>}
                            </div>
                          </div>
                        </div>

                        <div className="bulk-order-card-body">
                          <div>
                            <div className="text-muted small mb-1">Partner pricing</div>
                            <PriceWithSale
                              price={product.price}
                              salePrice={product.salePrice}
                              saleStartAt={product.saleStartAt}
                              saleEndAt={product.saleEndAt}
                              tierPrice={tierPrice}
                              showTierTag={Boolean(tierPrice)}
                              forceBasePrice={isTieredUser && !tierPrice}
                            />
                          </div>
                          <div className="bulk-order-qty">
                            <Form.Label className="mb-1">Qty</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={getQty(product._id)}
                              onChange={(e) =>
                                handleQtyChange(product._id, e.target.value)
                              }
                            />
                            <div className="text-muted small mt-1">
                              No minimum quantity
                            </div>
                          </div>
                          <div className="bulk-order-action">
                            <Button variant="primary" onClick={() => addItem(product)}>
                              Add to cart
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>

              {pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    {[...Array(pages).keys()].map((x) => {
                      const page = x + 1;
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
